<template>
    <div>
        <h3 class="text-body font-semibold mb-16">{{ $t(`settings.${moduleInfo.templateModel}.template_name`) }}</h3>

        <div class="flex items-center justify-between mb-16">
            <div class="flex-1 flex justify-between items-center group">
                <click-edit-input ref="editTitle"
                                  :full-width="true"
                                  :disabled="!canManage"
                                  :value="template.titleLocale"
                                  class="flex items-center w-full pr-16 h-32"
                                  @input="editTemplateTitle"
                />

                <button v-if="canManage"
                        class="flex items-center justify-center text-primary-80 group-hover:text-primary h-32 w-32"
                        :title="$t(`settings.${this.moduleInfo.templateModel}.edit_name`)"
                        @click="$emit('edit')"
                >
                    <svg-icon class="max-w-20 max-h-20" type="edit"/>
                </button>
            </div>

            <div class="flex items-center" v-if="canManage">
                <button :title=" $t(`settings.${this.moduleInfo.templateModel}.duplicate.title`, {
                            type: this.$t(`settings.${this.moduleInfo.templateModel}.single`)
                        })"
                        v-if="allowDuplicate"
                        class="text-primary-80 hover:text-primary w-32 w-48"
                        @click="startDuplicate(template)">
                    <svg-icon class="mx-auto max-w-20 max-h-20" type="content-copy"/>
                </button>
                <button :title="$t(`settings.${this.moduleInfo.templateModel}.delete.title`)"
                        class="text-primary-80 hover:text-semantic-error"
                        @click="confirmDelete(template)">
                    <svg-icon class="max-w-20 max-h-20" type="delete"/>
                </button>
            </div>
        </div>

        <popup-modal-confirm
            :confirmText="$t('general.delete_subject', {subject: deleting.title[currentLocale]})"
            :message="deleteTemplateMessage"
            show-exclamation
            :title="$t(`settings.${this.moduleInfo.templateModel}.delete.title`)"
            v-if="deleting"
            :working="performingDeleteRequest"
            @close="cancelDelete"
            @confirm="deleteTemplate(deleting)"
        />

        <interview-template-duplicate
            v-if="allowDuplicate"
            :template="duplicating"
            :working="performingDuplicateRequest"
            @close="duplicating = null"
            @confirm="duplicateTemplate(duplicating)"/>
    </div>
</template>

<script>
import {ADD_NOTIFICATION} from '../state/mutation-types';
import {mapState, mapActions} from 'pinia';
import {useMainStore} from '../state/store';
import {useNotificationsStore} from '../state/modules/notifications';
import PopupModal from './ui/PopupModal.vue';
import Loading from './ui/Loading.vue';
import ClickEditInput from './forms/ClickEditInput.vue';
import SvgIcon from './ui/SvgIcon.vue';
import PopupModalConfirm from './ui/PopupModalConfirm.vue';
import InterviewTemplateDuplicate from './InterviewTemplateDuplicate.vue';
import {normalizeJson} from '../../shared/helpers';
import {useRepo} from "pinia-orm";
import CompetenceTemplate from "../models/CompetenceTemplate";
import InterviewTemplate from "../models/InterviewTemplate";
import FilterGroup from "../models/FilterGroup";

export default {
    components: {
        InterviewTemplateDuplicate,
        PopupModalConfirm,
        SvgIcon,
        ClickEditInput,
        Loading,
        PopupModal
    },
    props: {
        value: null,
        countItems: 0,
        service: null,
    },
    data() {
        return {
            deleting: null,
            performingDeleteRequest: false,
            performingDuplicateRequest: false,
            duplicating: null,
            models: {
                'competence-templates': CompetenceTemplate,
                'interview-templates': InterviewTemplate,
                'filter-groups': FilterGroup,
            }
        };
    },
    computed: {
        ...mapState(useMainStore, ['selectedLocale', 'templateModuleInfo']),
        moduleInfo() {
            return this.templateModuleInfo;
        },
        model() {
            return useRepo(this.models[this.moduleInfo.templateModel]);
        },
        locale() {
            return this.moduleInfo.title.locale || null;
        },
        currentLocale() {
            return this.selectedLocale;
        },
        allowDuplicate() {
            return this.moduleInfo.allowDuplicate;
        },
        template: {
            get() {
                return this.value;
            },
            set(value) {
                this.value = value;
            },
        },
        deleteTemplateMessage() {
            if (this.countItems > 0 && this.deleting) {
                return this.$t(`settings.${this.moduleInfo.templateModel}.delete.message`, {
                    subject: this.deleting.title[this.currentLocale],
                    amount: this.countItems,
                    type: this.$t(`settings.${this.moduleInfo.model}.title`)
                });
            }

            return this.$t(`settings.${this.moduleInfo.templateModel}.delete.message_no_${this.moduleInfo.model}`, {
                subject: this.deleting.title[this.currentLocale],
                type: this.$t(`settings.${this.moduleInfo.model}.title`)
            });
        },
        canManage() {
            return Number.isInteger(this.template.id);
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        editTemplateTitle(input) {
            this.service.update(this.template.id, {
                data: {
                    attributes: {title: {[this.locale]: input}}
                },
            }).then(() => {
                const currentState = this.model.where('id', this.template.id).first()
                if (!currentState) return;

                let titleAttribute = {...currentState.title};
                titleAttribute[this.locale] = input;

                this.model.save({
                    id: this.template.id,
                    title: titleAttribute
                });
            }).then(this.template.title[this.locale] = input)
        },
        confirmDelete(template) {
            this.deleting = template;
        },
        cancelDelete() {
            this.deleting = null;
        },
        startDuplicate(template) {
            this.duplicating = template;
        },
        duplicateTemplate(template) {
            this.performingDuplicateRequest = true;

            this.service.duplicate(template.id).then(result => {
                this.model.save(normalizeJson(result.data));
            }).finally(() => {
                this.duplicating = null;
                this.performingDuplicateRequest = false;
            });
        },
        deleteTemplate(template) {
            this.performingDeleteRequest = true;

            const successMessage = this.$t('settings.interview-templates.delete.success', { type: this.$t(`settings.${this.moduleInfo.templateModel}.single`).toLowerCase() });

            this.service.destroy(template.id)
                .then(() => {
                    return this.model.destroy(template.id);
                })
                .finally(() => {
                    this.deleting = null;
                    this.performingDeleteRequest = false;
                    this.$emit('reset');

                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: successMessage
                    });
                });
        },
    },
};
</script>
